import fp from 'lodash/fp';
import { createActions } from 'redux-actions';
import { put, select } from 'redux-saga/effects';
import {
  apiActionResult,
  apiActions as commonApiActions,
  apiResourceActionCreator
} from '../../common';
import { authTokenSelector } from '../../session';
import { API_ENDPOINT } from './endpoints';
import getBusinessEmailJobStatusesConfig from './getBusinessEmailJobStatuses';

const actions = createActions({
  TASK: {
    SEND_REMINDER: (taskId, businessId, targetUserIds = null) => ({
      taskId,
      businessId,
      targetUserIds
    }),
    API: {
      SEND_REMINDER: apiResourceActionCreator('reminders')
    }
  }
});

/**
 * Dispatches an HTTP POST request targeting the business task reminder endpoint with taskId in the URL path.
 */
export const apiAction = (authToken, businessId, taskId, payload) =>
  commonApiActions.authenticated.post(
    `${API_ENDPOINT}/business/${businessId}/reminder/${taskId}`,
    authToken,
    payload,
    actions.task.api.sendReminder
  );

/**
 * Saga worker that dispatches a task reminder request and, upon success,
 * triggers an update for the business email job statuses.
 */
function* sendReminder(action) {
  const authToken = yield select(authTokenSelector);
  const { taskId, businessId, targetUserIds } = action.payload;

  // JSON body now strictly contains targetUserIds (or empty object)
  const payload =
    targetUserIds && targetUserIds.length > 0 ? { targetUserIds } : {};

  yield put(apiAction(authToken, businessId, taskId, payload));

  const result = yield apiActionResult(actions.task.api.sendReminder);

  if (result.success && businessId) {
    yield put(
      getBusinessEmailJobStatusesConfig.action.task.getBusinessEmailJobStatuses(
        businessId
      )
    );
  }
}

const attachReminders = (payload) => (task) => {
  const reminders = fp.filter(({ taskId }) => taskId === task.id)(
    payload.reminders
  );
  return {
    ...task,
    reminders: task.reminders.concat(reminders)
  };
};

/**
 * Redux configuration mapping actions, sagas, and state slice reducers
 * for processing and recording task reminders.
 */
const configuration = {
  name: 'sendReminder',
  action: actions,
  sagas: sendReminder,
  reducers: {
    request: (state, action) => ({
      ...state,
      isSendingReminder: true,
      is429Error: false
    }),
    success: (state, action) => ({
      ...state,
      businessTasks: fp.map(attachReminders(action.payload))(
        state.businessTasks
      ),
      teamTasks: fp.map(attachReminders(action.payload))(state.teamTasks),
      isSendingReminder: false,
      is429Error: false
    }),
    failed: (state, action) => {
      const is429Error =
        action &&
        action.meta &&
        action.meta.response &&
        String(action.meta.response.status) === '429';

      return {
        ...state,
        isSendingReminder: false,
        is429Error: is429Error
      };
    }
  }
};

export default configuration;



// WEBPACK FOOTER //
// ./src/state/modules/task/configurations/sendReminder.js